import { useSession, useUser } from "@clerk/react";
import { usePostHog } from "@posthog/react";
import { useEffect, useRef } from "react";

import {
  captureAuthSessionStarted,
  deriveSessionMethod,
} from "../lib/auth-analytics";

// ONE fixed key holding only the last reported Clerk session id (overwritten,
// never accumulated). localStorage so a 30-day session opened in another tab
// does not re-fire.
const STORAGE_KEY = "auth.session_started.last";

/** Read the `fva` (factor verification age) claim from a session JWT. */
export function decodeFva(token: string | null | undefined): unknown {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
    const claims = JSON.parse(json) as { fva?: unknown };
    return claims.fva ?? null;
  } catch {
    return null;
  }
}

function alreadyReported(sessionId: string): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === sessionId;
  } catch {
    return false;
  }
}

function markReported(sessionId: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, sessionId);
  } catch {
    // Storage unavailable: the in-memory ref still dedupes this page load.
  }
}

/**
 * `auth.session_started` once per Clerk session (spec §8). Mount once, at the
 * app root. The session id is only a dedupe key — it is never sent (I6).
 */
export function useSessionStartedAnalytics() {
  const { isLoaded: sessionLoaded, session } = useSession();
  const { isLoaded: userLoaded, user } = useUser();
  const posthog = usePostHog();
  const reported = useRef(new Set<string>());

  useEffect(() => {
    if (!sessionLoaded || !userLoaded || !session || !user) return;
    const sessionId = session.id;
    if (reported.current.has(sessionId) || alreadyReported(sessionId)) return;
    reported.current.add(sessionId);
    markReported(sessionId);

    void (async () => {
      let fva: unknown = null;
      try {
        fva = decodeFva(await session.getToken());
      } catch {
        // Unreadable token: method degrades to passkey-or-unknown.
      }
      const method = deriveSessionMethod({
        passkeys: (user as { passkeys?: Array<{ lastUsedAt?: Date | null }> })
          .passkeys,
        sessionCreatedAt: session.createdAt,
        fva,
      });
      captureAuthSessionStarted(
        (event, props) => posthog?.capture?.(event, props),
        method,
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLoaded, userLoaded, session?.id, user?.id]);
}
